import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import { BarChart } from "@mui/x-charts/BarChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    toNum,
} from "../../bids/analytics/format";
import { Game } from "../../../models/games";
import { UeFilters, UeFiltersState } from "../components/UeFilters";
import { TermHint } from "../components/TermHint";
import { ueGmvByGameApi, ueMarketplaceRolesApi, ueSellerBuyerApi } from "../unitEconomicsApi";
import { UeCampaignRoleSlice } from "../../../models/unitEconomics";

type Props = {
    isActive: boolean;
    filters: UeFiltersState;
    onFiltersChange: (next: UeFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function GmvByGameTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;
    const [state, refresh] = useAsyncBlock(
        () =>
            ueGmvByGameApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showGame
            />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="gmv" /> и маржа по играм
                        </>
                    }
                    subtitle={
                        <>
                            Оборот, комиссия и <TermHint term="takeRate" /> в разрезе игр. Отдельно — оборот через
                            поставщиков и прибыль с учётом фактической себестоимости выкупа.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Сравнивайте не только <TermHint term="gmv" />, но и{" "}
                                    <TermHint term="takeRate" /> — где маржа выгоднее
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    «Прибыль факт» отличается от комиссии только для предметов поставщиков:
                                    вместо наценки на витрине берётся цена минус уплаченное поставщику
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            <TermHint term="gmv" /> игры
                                        </>
                                    ),
                                    ifGrows: "игра тянет оборот; проверьте, не падает ли take rate.",
                                    ifFalls: "спрос/предложение в игре слабеют.",
                                },
                                {
                                    signal: (
                                        <>
                                            <TermHint term="takeRate" /> игры
                                        </>
                                    ),
                                    ifGrows: "маржа с оборота игры выше — правила комиссий работают.",
                                    ifFalls: "игры с большим GMV могут быть «дешёвыми» по марже.",
                                },
                            ]}
                        />
                    }
                >
                    {d.games.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <>
                            <BarChart
                                height={260}
                                xAxis={[{ data: d.games.map((g) => g.gameName), scaleType: "band" }]}
                                series={[
                                    {
                                        data: d.games.map((g) => g.gmv / 100),
                                        label: "GMV ₽",
                                        color: "#1976d2",
                                    },
                                    {
                                        data: d.games.map((g) => toNum(g.externalGmv) / 100),
                                        label: "Через поставщиков ₽",
                                        color: "#90caf9",
                                    },
                                    {
                                        data: d.games.map((g) => g.profit / 100),
                                        label: "Комиссия ₽",
                                        color: "#2e7d32",
                                    },
                                ]}
                                margin={{ left: 70, right: 20, top: 20, bottom: 60 }}
                            />
                            <ScrollTable>
                                <Table size="sm" hoverRow>
                                    <thead>
                                        <tr>
                                            <th>Игра</th>
                                            <th style={{ textAlign: "right" }}>Ордера</th>
                                            <th style={{ textAlign: "right" }}>
                                                <TermHint term="gmv" />
                                            </th>
                                            <th style={{ textAlign: "right" }}>Через поставщиков</th>
                                            <th style={{ textAlign: "right" }}>Комиссия</th>
                                            <th style={{ textAlign: "right" }}>Прибыль факт</th>
                                            <th style={{ textAlign: "right" }}>
                                                <TermHint term="takeRate">Take %</TermHint>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.games.map((g) => (
                                            <tr key={g.gameId}>
                                                <td>{g.gameName}</td>
                                                <td style={{ textAlign: "right" }}>{formatInt(g.orderCount)}</td>
                                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(g.gmv)}</td>
                                                <td style={{ textAlign: "right" }}>
                                                    {formatMoneyKopecks(g.externalGmv)}
                                                    {toNum(g.gmv) > 0 && toNum(g.externalGmv) > 0 && (
                                                        <Typography level="body-xs">
                                                            {formatRatePercent(toNum(g.externalGmv) / toNum(g.gmv))}
                                                        </Typography>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(g.profit)}</td>
                                                <td style={{ textAlign: "right" }}>
                                                    {formatMoneyKopecks(g.actualProfit ?? g.profit)}
                                                </td>
                                                <td style={{ textAlign: "right" }}>{formatRatePercent(g.takeRate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </ScrollTable>
                        </>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function SellerBuyerTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;
    const [state, refresh] = useAsyncBlock(
        () =>
            ueSellerBuyerApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                take: filters.take,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.take]
    );
    const d = state.data;

    const sideTable = (
        title: string,
        rows: NonNullable<typeof d>["topSellers"]
    ) => (
        <>
            <Typography level="title-sm">{title}</Typography>
            {rows.length === 0 ? (
                <EmptyState />
            ) : (
                <ScrollTable>
                    <Table size="sm" hoverRow>
                        <thead>
                            <tr>
                                <th>Пользователь</th>
                                <th style={{ textAlign: "right" }}>Ордера</th>
                                <th style={{ textAlign: "right" }}>
                                    <TermHint term="fee" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={String(u.userId)}>
                                    <td>
                                        <Link
                                            href={`/app/users/${u.userId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {u.userName || String(u.userId)}
                                        </Link>
                                    </td>
                                    <td style={{ textAlign: "right" }}>{formatInt(u.orderCount)}</td>
                                    <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.tradeProfit)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </ScrollTable>
            )}
        </>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showGame
                showTake
            />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="seller" /> vs <TermHint term="buyer" />
                        </>
                    }
                    subtitle={
                        <>
                            Баланс сторон маркетплейса: активные продавцы и покупатели,{" "}
                            <TermHint term="gmv" /> и комиссии.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Ratio sellers/buyers — если сильно &lt; 1, спрос превышает предложение
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="fee" /> суммируется по позициям стороны (у покупателя и
                                    продавца одна и та же комиссия ордера)
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            Активные <TermHint term="seller">sellers</TermHint> /{" "}
                                            <TermHint term="buyer">buyers</TermHint>
                                        </>
                                    ),
                                    ifGrows: "сторона рынка оживает — смотрите, не разъезжается ли ratio.",
                                    ifFalls: "одна из сторон сжимается — риск ликвидности.",
                                },
                                {
                                    signal: "Seller/Buyer ratio",
                                    ifGrows: "больше предложения относительно спроса.",
                                    ifFalls: "спрос доминирует — может расти fill time / цены.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint
                            label={<TermHint term="seller">Продавцы</TermHint>}
                            value={formatInt(d.activeSellers)}
                            color="primary"
                        />
                        <MetricHint
                            label={<TermHint term="buyer">Покупатели</TermHint>}
                            value={formatInt(d.activeBuyers)}
                            color="success"
                        />
                        <MetricHint label="S/B ratio" value={toNum(d.sellerBuyerRatio).toFixed(2)} />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="gmv" /> sellers
                                </>
                            }
                            value={formatMoneyKopecks(d.sellerGmv)}
                        />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="gmv" /> buyers
                                </>
                            }
                            value={formatMoneyKopecks(d.buyerGmv)}
                        />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="fee" /> (buyer side)
                                </>
                            }
                            value={formatMoneyKopecks(d.buyerFee)}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        {sideTable("Топ продавцов", d.topSellers)}
                        {sideTable("Топ покупателей", d.topBuyers)}
                    </Box>
                </AnalyticsCard>
            )}
        </Box>
    );
}

function MarketplaceRoleSliceCard({
    title,
    slice,
}: {
    title: React.ReactNode;
    slice: UeCampaignRoleSlice;
}) {
    return (
        <AnalyticsCard
            title={title}
            subtitle={`${formatInt(slice.count)} · ${formatRatePercent(slice.share)} активных трейдеров`}
            snapshotLabel="period"
            howToRead={
                <List size="sm" marker="disc">
                    <ListItem>
                        <Typography level="body-sm">
                            Count / Share — от тех, кто покупал или продавал в периоде (без пересечения
                            ролей)
                        </Typography>
                    </ListItem>
                    <ListItem>
                        <Typography level="body-sm">
                            <TermHint term="ltv" /> — средний contribution роли за период. Комиссия
                            ордера в contribution на стороне buyer
                        </Typography>
                    </ListItem>
                    <ListItem>
                        <Typography level="body-sm">
                            <TermHint term="gmvBuy" /> / <TermHint term="gmvSell" /> — оборот ордеров,
                            где пользователь buyer / seller
                        </Typography>
                    </ListItem>
                </List>
            }
            watchFor={
                <WatchSignals
                    items={[
                        {
                            signal: (
                                <>
                                    <TermHint term="ltv" /> роли
                                </>
                            ),
                            ifGrows: "эта роль приносит больше прибыли на человека.",
                            ifFalls: "роль дешевеет — смотрите GMV и комиссии.",
                        },
                        {
                            signal: (
                                <>
                                    <TermHint term="contribution" /> / GMV
                                </>
                            ),
                            ifGrows: "роль крутит больше оборота и прибыли.",
                            ifFalls: "роль даёт меньше маржи — смотрите fee на buyer.",
                        },
                    ]}
                />
            }
        >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <MetricHint label="Count" value={formatInt(slice.count)} />
                <MetricHint label="Share" value={formatRatePercent(slice.share)} />
                <MetricHint label={<TermHint term="ltv" />} value={formatMoneyKopecks(slice.ltv)} />
                <MetricHint
                    label={<TermHint term="contribution" />}
                    value={formatMoneyKopecks(slice.contribution)}
                    color="success"
                />
                <MetricHint
                    label={<TermHint term="gmvBuy" />}
                    value={formatMoneyKopecks(slice.gmvBuy)}
                />
                <MetricHint
                    label={<TermHint term="gmvSell" />}
                    value={formatMoneyKopecks(slice.gmvSell)}
                />
            </Box>
        </AnalyticsCard>
    );
}

export function MarketplaceRolesTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueMarketplaceRolesApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && d.activeTraders === 0 && (
                <EmptyState hint="За период нет завершённых ордеров." />
            )}
            {d && d.activeTraders > 0 && (
                <>
                    <AnalyticsCard
                        title="Роли в маркетплейсе"
                        subtitle={`Активных трейдеров ${formatInt(d.activeTraders)}. Exclusive: покупки без продаж / продажи без покупок / оба.`}
                        snapshotLabel="period"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Buyer-only</strong> — покупки без продаж;{" "}
                                        <strong>Seller-only</strong> — продажи без покупок;{" "}
                                        <TermHint term="hybrid" /> — и то и другое. Share — от
                                        активных трейдеров периода, не от всех пользователей
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <TermHint term="gmvBuy" /> / <TermHint term="gmvSell" /> — оборот
                                        всей стороны (buyer-only + hybrid / seller-only + hybrid). Не
                                        сумма buy+sell: гибрид входит в оба
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="hybrid" />,
                                        ifGrows: "больше двусторонних пользователей — здоровее маркетплейс.",
                                        ifFalls: "активность односторонняя: только покупают или только продают.",
                                    },
                                    {
                                        signal: "Seller-only",
                                        growsIsGood: false,
                                        ifGrows: "много продавцов без покупок — contribution с fee на них почти нет.",
                                        ifFalls: "меньше «чистых» селлеров, больше buyer/hybrid.",
                                    },
                                    {
                                        signal: (
                                            <>
                                                <TermHint term="gmvBuy" /> /{" "}
                                                <TermHint term="gmvSell" />
                                            </>
                                        ),
                                        ifGrows: "сторона крутит больше оборота.",
                                        ifFalls: "оборот стороны падает.",
                                    },
                                ]}
                            />
                        }
                    >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                            <MetricHint label="Трейдеры" value={formatInt(d.activeTraders)} />
                            <MetricHint
                                label="Buyer-only"
                                value={formatInt(d.buyerOnly.count)}
                                hint={formatRatePercent(d.buyerOnly.share)}
                            />
                            <MetricHint
                                label="Seller-only"
                                value={formatInt(d.sellerOnly.count)}
                                hint={formatRatePercent(d.sellerOnly.share)}
                            />
                            <MetricHint
                                label={<TermHint term="hybrid" />}
                                value={formatInt(d.hybrid.count)}
                                hint={formatRatePercent(d.hybrid.share)}
                            />
                            <MetricHint
                                label={<TermHint term="gmvBuy" />}
                                value={formatMoneyKopecks(d.gmvBuy)}
                            />
                            <MetricHint
                                label={<TermHint term="gmvSell" />}
                                value={formatMoneyKopecks(d.gmvSell)}
                            />
                        </Box>
                    </AnalyticsCard>
                    <MarketplaceRoleSliceCard title="Покупатели без продаж" slice={d.buyerOnly} />
                    <MarketplaceRoleSliceCard title="Продавцы без покупок" slice={d.sellerOnly} />
                    <MarketplaceRoleSliceCard title="Гибриды" slice={d.hybrid} />
                </>
            )}
        </Box>
    );
}
